// @ts-nocheck
// TODO: tighten types after migration

import React, { useState, useEffect, useMemo } from "react";
import "./ReleaventTopics.css";
import { useParams, NavLink } from "react-router-dom";
import { API_ENDPOINTS } from "../config/api";
import { getBlogPostPath } from "../utils/blogRoutes";


import { robustFetch } from "../utils/apiUtils";


export default function RelevantPosts() {
  const params = useParams();  
  // Handle both old format (/blog/:blogid) and new format (/blog/:categoryName/:slug)
  let slug;
  if (params.slug) {
    // New format: /blog/:categoryName/:slug
    slug = params.slug;
  } else {
    // Old format: /blog/:blogid
    slug = Object.values(params).join();
  }  

  // ========== MONGODB POSTS FETCHING START ==========
  const [mongoPosts, setMongoPosts] = useState([]);
  const [mongoLoading, setMongoLoading] = useState(true);
  const [mongoError, setMongoError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Fetch posts from MongoDB BlogDb
  useEffect(() => {
    let hasCachedData = false;
    try {
      const cached = localStorage.getItem('blog_posts_all_v1') || sessionStorage.getItem('blog_posts_all_v1');
      if (cached) {
        const cachedPosts = JSON.parse(cached);
        if (Array.isArray(cachedPosts) && cachedPosts.length > 0) {
          setMongoPosts(cachedPosts);
          setMongoLoading(false);
          hasCachedData = true;
        }
      }
    } catch(e) {}

    const fetchMongoPosts = async (retryCount = 0) => {
      try {
        if (!hasCachedData) setMongoLoading(true);
        setMongoError(null);
        
        const response = await robustFetch(API_ENDPOINTS.BLOG.POSTS);
        
        if (!response.ok) {
          if (response.status === 404 || response.status === 503) {
            if (!hasCachedData) setMongoPosts([]);
            setMongoLoading(false);
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const postsData = await response.json();
        
        if (postsData && Array.isArray(postsData)) {
          const publishedPosts = postsData.filter(post => post.isPublished === true);
          
          if (publishedPosts.length > 0) {
            const formattedPosts = publishedPosts.map(post => ({
              node: {
                id: post._id || post.id,
                slug: post.slug,
                title: post.title,
                // Preserve full postTopic object with _id, topic, topicSlug for proper comparison
                postTopic: post.postTopic || null,
                postNumber: post.postNumber || 0,
                createdAt: post.createdAt,
                categories: post.category ? (Array.isArray(post.category) ? post.category : [post.category]) : [],
                coverImage: post.coverImage || null
              }
            }));
            
            try {
              localStorage.setItem('blog_posts_all_v1', JSON.stringify(formattedPosts));
              sessionStorage.setItem('blog_posts_all_v1', JSON.stringify(formattedPosts));
            } catch(e) {}

            setMongoPosts(formattedPosts);
          } else if (!hasCachedData) {
            setMongoPosts([]);
          }
        } else if (!hasCachedData) {
          setMongoPosts([]);
        }
        setMongoLoading(false);
      } catch (err) {
        console.error(`Error fetching MongoDB posts (Attempt ${retryCount + 1}):`, err);
        if (retryCount < 4) {
          setTimeout(() => fetchMongoPosts(retryCount + 1), 2000);
        } else {
          if (!hasCachedData) {
            setMongoError(err);
            setMongoPosts([]);
          }
          setMongoLoading(false);
        }
      }
    };

    fetchMongoPosts();
  }, []);
  // ========== MONGODB POSTS FETCHING END ==========

  // Consolidate posts (only MongoDB now)
  const allPosts = useMemo(() => {
    let combined = [];
    
    // Add MongoDB posts
    if (mongoPosts.length > 0) {
      combined.push(...mongoPosts);
    }
    
    // Sort by createdAt descending
    combined.sort((a, b) => {
      const dateA = new Date(a.node ? a.node.createdAt : "");
      const dateB = new Date(b.node ? b.node.createdAt : "");
      return dateB - dateA;
    });

    return combined;
  }, [mongoPosts]);

  // 1️⃣ Find current post by slug
  const currentPost = useMemo(() => {
    return allPosts.find((post) => post.node && post.node.slug === slug);
  }, [allPosts, slug]);

  // 2️⃣ Get current PostTopic - compare by _id, topicSlug, or topic
  const currentTopicIdentifier = useMemo(() => {
    if (!currentPost || !currentPost.node) return null;
    const postTopic = currentPost.node.postTopic;
    if (!postTopic) return null;
    
    if (typeof postTopic === 'object' && postTopic._id) return String(postTopic._id);
    if (typeof postTopic === 'object' && postTopic.id) return String(postTopic.id);
    if (typeof postTopic === 'object' && postTopic.topicSlug) return postTopic.topicSlug;
    if (typeof postTopic === 'object' && postTopic.topic) return postTopic.topic;
    if (typeof postTopic === 'string') return postTopic;
    
    return null;
  }, [currentPost]);

  // 3️⃣ Filter and sort related posts with the same PostTopic (excluding current post)
  const displayPosts = useMemo(() => {
    if (!currentTopicIdentifier) return [];

    const relatedPosts = allPosts.filter((post) => {
      if (!post.node || post.node.slug === slug) return false;
      const pt = post.node.postTopic;
      let postTopicIdentifier = null;
      if (typeof pt === 'object' && pt._id) postTopicIdentifier = String(pt._id);
      else if (typeof pt === 'object' && pt.id) postTopicIdentifier = String(pt.id);
      else if (typeof pt === 'object' && pt.topicSlug) postTopicIdentifier = pt.topicSlug;
      else if (typeof pt === 'object' && pt.topic) postTopicIdentifier = pt.topic;
      else if (typeof pt === 'string') postTopicIdentifier = pt;

      return postTopicIdentifier === currentTopicIdentifier;
    });

    const sorted = relatedPosts.sort((a, b) => {
      const numA = a.node && a.node.postNumber ? a.node.postNumber : 0;
      const numB = b.node && b.node.postNumber ? b.node.postNumber : 0;
      return numB - numA;
    });

    // Limit to latest 4 posts
    return sorted.slice(0, 4);
  }, [allPosts, currentTopicIdentifier, slug]);

  // 4️⃣ Automatic 5-second interval to switch active blog thumbnail
  useEffect(() => {
    if (displayPosts.length <= 1) return;

    const timer = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % displayPosts.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [displayPosts.length]);

  // Helper to resolve cover image URL
  const getCoverImageUrl = (coverImage) => {
    if (!coverImage) return "";
    let rawUrl = "";
    if (typeof coverImage === "string") {
      rawUrl = coverImage;
    } else if (typeof coverImage === "object" && coverImage.url) {
      rawUrl = coverImage.url;
    }
    if (!rawUrl) return "";
    return rawUrl.startsWith("http")
      ? rawUrl
      : `https://www.harivishnu.in${rawUrl.startsWith("/") ? "" : "/"}${rawUrl}`;
  };

  if (mongoLoading && allPosts.length === 0) {
    return (
      <div className="related-posts-container mb-10 h-fit w-full p-6 sm:p-5 border-[3px] border-solid border-[var(--slider-color)] rounded-[30px] bg-[var(--bg-color)] shadow-sm">
        <h2 className="text-[1.35rem] font-bold text-[var(--text-color)] mt-1 mb-4">Latest from our follow-ups</h2>
        <div className="skeleton-loader">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton-item">
              <div className="skeleton-text"></div>
              <div className="skeleton-text"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  if (mongoError && allPosts.length === 0 && !mongoLoading) {
    return <p className="text-red-500 p-4">Error: {mongoError.message}</p>;
  }
  
  if (!allPosts || allPosts.length === 0 || !currentPost) {
    return null;
  }

  return (
    <div className="related-posts-container mb-10 h-fit w-full p-6 sm:p-5 border-[3px] border-solid border-[var(--slider-color)] rounded-[30px] bg-[var(--bg-color)] shadow-sm font-inter">
      <h2 className="text-[1.35rem] sm:text-[1.5rem] font-bold text-[var(--text-color)] mt-1 mb-5 tracking-tight">
        Latest from our follow-ups
      </h2>
      <div className="flex flex-col relative space-y-4">
        {displayPosts.length === 0 ? (
          <p className="italic opacity-70 ml-2 text-[var(--text-color)]">No related posts found</p>
        ) : (
          <>
            {displayPosts.map((track, index) => {
              const isLast = index === displayPosts.length - 1;
              const isActive = index === (activeIndex % displayPosts.length);
              const coverImageUrl = getCoverImageUrl(track.node.coverImage);

              return (
                <div 
                  key={track.node.id} 
                  className={`relative group ${!isLast ? 'border-b border-gray-100 dark:border-gray-800/60 pb-4' : 'pb-1'}`}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <NavLink
                    to={getBlogPostPath(track.node)}
                    className="flex flex-col no-underline text-inherit"
                    onClick={() => {
                      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                    }}
                  >
                    {/* Post Number Header Container (Text-width bounded for underline & arrow) */}
                    <div className="inline-flex flex-col w-fit mb-1.5">
                      <div className="inline-flex items-center gap-1.5 w-fit">
                        {/* Post Number - Blue (#47b5ff) when active */}
                        <span className={`text-[0.78rem] font-bold uppercase tracking-wider transition-colors duration-200 ${
                          isActive ? 'text-[#47b5ff]' : 'text-gray-400 dark:text-gray-500 group-hover:text-[#47b5ff]'
                        }`}>
                          POST {track.node.postNumber}
                        </span>

                        {/* Small right arrow on hover in blue color (#47b5ff) */}
                        <span className="text-[#47b5ff] text-[1.1rem] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 leading-none flex items-center">
                          <i className="fi fi-rr-arrow-small-right"></i>
                        </span>
                      </div>

                      {/* Underline progress bar matching exact text width, denoting 5s progress for active post */}
                      <div className={`w-full h-[1.5px] rounded-full overflow-hidden mt-0.5 transition-colors duration-200 ${
                        isActive ? 'bg-gray-200/35 dark:bg-gray-700/35' : 'bg-gray-200/20 dark:bg-gray-800/20'
                      }`}>
                        <div 
                          key={`progress-${activeIndex}-${track.node.id}`}
                          className="h-full bg-[#47b5ff]"
                          style={{
                            animation: isActive ? 'progressLine 5s linear forwards' : 'none',
                            width: isActive ? '0%' : '0%'
                          }}
                        />
                      </div>
                    </div>

                    {/* Thumbnail Image - Displayed BETWEEN post number and title with ultra-smooth transition */}
                    <div className={`followup-thumbnail-grid ${isActive ? 'is-active' : ''}`}>
                      <div className="followup-thumbnail-inner">
                        <div className="overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-md">
                          <img
                            src={coverImageUrl || "/images/home_images/Logo.png"}
                            alt={track.node.title}
                            className="w-full h-[170px] sm:h-[190px] object-cover rounded-2xl transition-transform duration-500 hover:scale-105"
                            onError={(e) => {
                              e.currentTarget.src = "/images/home_images/Logo.png";
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className={`text-[1.05rem] sm:text-[1.1rem] font-semibold leading-snug tracking-tight m-0 transition-colors duration-200 ${
                      isActive ? '!text-[var(--text-color)] !font-bold' : '!text-[var(--text-color)] opacity-85 group-hover:!text-[#47b5ff]'
                    }`}>
                      {track.node.title}
                    </h3>
                  </NavLink>
                </div>
              );
            })}
            
            <NavLink 
              to="/blog" 
              className="inline-flex items-center justify-center gap-1.5 mx-auto mt-4 mb-1 px-5 py-2.5 rounded-full border-[1px] border-solid border-[#47b5ff] text-[#47b5ff] text-[0.88rem] font-semibold no-underline transition-all duration-300 ease-in-out hover:bg-[#47b5ff] hover:text-white hover:scale-[1.02]"
              onClick={() => window.scrollTo(0, 0)}
            >
              <span>See all follow-ups</span>
              <i className="fi fi-rr-arrow-small-right text-[1.1rem] leading-none"></i>
            </NavLink>
          </>
        )}
      </div>
    </div>
  );
}

